import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {Registerer, RegistererState, SessionState, UserAgent, Web} from "sip.js";
import {toast} from "react-toastify";
import {SignInContext} from "./SignInContext";
import {ProjectRoles} from "./AppContext";
import {WebrtcRepository, webrtcErrorCode, webrtcErrorMessage} from "../repositories/WebrtcRepository";
import {WebrtcCallPopup} from "../components/webrtc/WebrtcCallPopup";

// Project roles that take calls in the browser
export const WEBRTC_ROLES = [ProjectRoles.ADMIN, ProjectRoles.MANAGER, ProjectRoles.AGENT];

const EXPECT_TIMEOUT_MS = 60 * 1000;
const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 2 * 60 * 1000;
// after sip.js registers, how long to wait for Asterisk to report the agent's endpoint as registered
const VERIFY_ATTEMPTS = 7;
const VERIFY_INTERVAL_MS = 1500;
const HEALTH_INTERVAL_MS = 10 * 1000;
const RECONNECT_DELAY_MS = 4000;
const REGISTER_EXPIRES = 300;

const defaultFactory = Web.defaultSessionDescriptionHandlerFactory();

// "browser calls on" is remembered per user, so they reconnect on the next login / page load
const enabledKey = (username) => `webrtc_enabled_${username}`;
export const isWebrtcEnabled = (username) => {
  try {
    return Boolean(username) && localStorage.getItem(enabledKey(username)) === "1";
  } catch (e) {
    return false;
  }
};
const setWebrtcEnabled = (username, enabled) => {
  try {
    if (enabled) localStorage.setItem(enabledKey(username), "1");
    else localStorage.removeItem(enabledKey(username));
  } catch (e) {
    // ignore blocked storage
  }
};

const outcomeLabel = (outcome) => String(outcome || "unknown").replaceAll("_", " ");

// Asterisk can return template TURN urls ("turns:YOUR_DOMAIN:5349"): RTCPeerConnection rejects the whole config
const cleanIceServers = (servers) => (servers || [])
  .map(server => {
    const urls = [].concat(server?.urls || []).filter(url => !/YOUR_DOMAIN/i.test(url));
    return urls.length ? {...server, urls} : null;
  })
  .filter(Boolean);

const digitsOf = (value) => String(value || "").replace(/\D/g, "");

const MIC_BLOCKED_MESSAGE = "Microphone is blocked for this site. Click the lock icon in the address bar, allow the "
  + "microphone, then click Connect again.";

// Microphone access before connecting. While the permission is "prompt" the browser asks the agent; once it is
// "denied" the browser no longer asks, the agent has to allow it in the site settings. -> null or an error message
const requestMicrophone = async () => {
  if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
    return "Browser calls need a secure connection (HTTPS): the microphone is not available on this page.";
  }
  try {
    const permission = await navigator.permissions?.query({name: "microphone"});
    if (permission?.state === "denied") return MIC_BLOCKED_MESSAGE;
  } catch (e) {
    // the Permissions API does not know "microphone" in every browser: getUserMedia below decides
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({audio: true});
    stream.getTracks().forEach(track => track.stop());
    return null;
  } catch (e) {
    if (e?.name === "NotFoundError" || e?.name === "OverconstrainedError") {
      return "No microphone was found. Connect a microphone and click Connect again.";
    }
    if (e?.name === "NotReadableError") {
      return "The microphone is in use by another application or could not be started.";
    }
    // NotAllowedError: dismissed or denied in the prompt, or blocked in the site settings
    return "Microphone access is needed for browser calls. Allow the microphone when the browser asks, or click the "
      + "lock icon in the address bar to allow it, then click Connect again.";
  }
};

const WebrtcContext = createContext({
  status: "disconnected", error: null, call: null,
  connect: async () => false, disconnect: () => {}, attachProject: () => {}, answer: () => {}, reject: () => {}, hangup: () => {},
  toggleMute: () => {}, takeOverCall: async () => {}, callCustomer: async () => {}, redirectCall: async () => false,
});

export const useWebrtc = () => useContext(WebrtcContext);

// The agent's browser phone (sip.js user agent registered over WSS straight to Asterisk, as on the landing page), kept for the whole app so it survives route
// changes. status: disconnected | connecting | verifying | registered | unregistered | failed. "registered" ("Connected")
// means Asterisk itself reports the agent's endpoint as registered, not only that sip.js got a 200 OK.
// call: the current incoming call {id, caller, conversationId, state: ringing | connecting | active, startedAt,
// muted, projectId, subProjectId}; the agent's own calls (take over / call customer) are answered automatically.
export const WebrtcProvider = ({children}) => {
  const {currentUser} = useContext(SignInContext);
  const [status, setStatus] = useState("disconnected");
  const [error, setError] = useState(null);
  const [call, setCall] = useState(null);

  const statusRef = useRef(status);
  statusRef.current = status;
  const uaRef = useRef(null);
  const configRef = useRef(null);
  const registererRef = useRef(null);
  // the current call: a sip.js Invitation
  const sessionRef = useRef(null);
  const mutedRef = useRef(false);
  const audioRef = useRef(null);
  const refreshTimerRef = useRef(null);
  // Bumped by every connect / stop: a pending proxy reconnect of an older phone gives up
  const generationRef = useRef(0);
  // the latest startPhone / connect, for the proxy reconnect (declared before them)
  const startPhoneRef = useRef(null);
  const connectRef = useRef(null);
  // calls the agent started (take over / call customer): answered without ringing
  const expectedRef = useRef([]);
  // registry project the phone was connected from, used to open a call's conversation
  const projectRef = useRef({});
  const aliveRef = useRef(true);
  // the agent's name as shown on calls (the backend falls back to the username)
  const displayNameRef = useRef(null);
  displayNameRef.current = [currentUser?.name, currentUser?.lastname].filter(Boolean).join(" ") || null;

  // The config (TURN credentials) expires: fetched on every connect and reconnect, and refreshed shortly before
  // ice_credentials_expire_in. Only ice_servers are used after registering (when answering).
  // configRef.current.proxy: the SIP proxy token of this fetch ({token, expiresAt, wsUrl}), when the proxy is on
  const fetchConfig = useCallback(async () => {
    const {projectId} = projectRef.current;
    const response = await WebrtcRepository.getMyWebrtc({projectId, displayName: displayNameRef.current});
    const data = response?.data?.webrtc;
    const proxy = response?.data?.proxy?.token && response?.data?.proxy?.wsUrl ? response.data.proxy : null;
    if (!data?.sip_uri || (!proxy && !data?.ws_url)) throw new Error("Browser calling is not configured for you");
    configRef.current = {...data, ice_servers: cleanIceServers(data.ice_servers), proxy};
    clearTimeout(refreshTimerRef.current);
    const expiresIn = Number(data?.ice_credentials_expire_in);
    if (expiresIn > 0) {
      refreshTimerRef.current = setTimeout(
        () => fetchConfig().catch(e => console.error("Failed to refresh the WebRTC config", e)),
        Math.max(30, expiresIn - 60) * 1000
      );
    }
    return configRef.current;
  }, []);

  const clearAudio = () => {
    if (audioRef.current) audioRef.current.srcObject = null;
  };

  // Ends a call in whatever state it is: reject while ringing / answering, BYE once established.
  const endSession = (session, ringingStatus = 480) => {
    if (!session) return;
    try {
      if (session.state === SessionState.Established) session.bye().catch(() => {});
      else if (session.state === SessionState.Initial || session.state === SessionState.Establishing) {
        session.reject({statusCode: ringingStatus}).catch(() => {});
      }
    } catch (e) {
      // already ended
    }
  };

  const stop = useCallback(() => {
    // a pending proxy reconnect belongs to this phone
    generationRef.current += 1;
    clearTimeout(refreshTimerRef.current);
    const ua = uaRef.current;
    const registerer = registererRef.current;
    uaRef.current = null;
    registererRef.current = null;
    endSession(sessionRef.current);
    sessionRef.current = null;
    (async () => {
      try {
        if (registerer?.state === RegistererState.Registered) await registerer.unregister();
      } catch (e) {
        // the socket may be gone already
      }
      try {
        await ua?.stop();
      } catch (e) {
        console.error(e);
      }
    })();
    clearAudio();
    setCall(null);
    setStatus("disconnected");
  }, []);

  const expectCall = (expected) => {
    const entry = {...expected, until: Date.now() + EXPECT_TIMEOUT_MS};
    expectedRef.current = [...expectedRef.current, entry];
    return entry;
  };
  const forgetCall = (entry) => {
    expectedRef.current = expectedRef.current.filter(item => item !== entry);
  };
  // Is this incoming call one the agent started (answered automatically)? By X-Call-Type:
  // - agent-outbound: "Call customer" leg, same X-Conversation-Id or X-To-Number
  // - agent-handover: "Take over call", same X-Conversation-Id
  // - agent-transfer (the bot transferred it here) / agent-group (rings every joined agent): never, they ring
  // Without X-Call-Type (the server's dialplan before call types): take over by X-Conversation-Id, call customer by
  // the "Call <number>" display name. TODO: drop this fallback once the new dialplan is deployed.
  const takeExpected = ({conversationId, callType, toNumber, caller}) => {
    const now = Date.now();
    expectedRef.current = expectedRef.current.filter(item => item.until > now);
    if (callType === "agent-transfer" || callType === "agent-group") return null;
    const sameConversation = (item) =>
      Boolean(item.conversationId && conversationId && String(item.conversationId) === String(conversationId));
    // tolerant of country code / leading zero differences
    const sameNumber = (item, number) => {
      const [a, b] = [digitsOf(item.number).replace(/^0+/, ""), digitsOf(number).replace(/^0+/, "")];
      return Boolean(a && b && (a.endsWith(b) || b.endsWith(a)));
    };
    const match = expectedRef.current.find(item => {
      if (callType === "agent-outbound") return item.callCustomer && (sameConversation(item) || sameNumber(item, toNumber));
      if (callType === "agent-handover") return !item.callCustomer && sameConversation(item);
      if (callType) return false;
      return item.callCustomer ? /^call\s/i.test(caller) : sameConversation(item);
    });
    if (match) forgetCall(match);
    return match || null;
  };

  const answer = useCallback(() => {
    const session = sessionRef.current;
    if (!session || session.state !== SessionState.Initial) return;
    setCall(current => current ? {...current, state: "connecting"} : current);
    session.accept({sessionDescriptionHandlerOptions: {constraints: {audio: true, video: false}}}).catch(e => {
      console.error(e);
      toast.error("Could not answer the call");
    });
  }, []);

  const reject = useCallback(() => endSession(sessionRef.current, 486), []);

  const hangup = useCallback(() => endSession(sessionRef.current), []);

  // mute = the microphone track stops sending
  const toggleMute = useCallback(() => {
    const peerConnection = sessionRef.current?.sessionDescriptionHandler?.peerConnection;
    if (!peerConnection) return;
    const muted = !mutedRef.current;
    mutedRef.current = muted;
    peerConnection.getSenders().forEach(sender => {
      if (sender.track?.kind === "audio") sender.track.enabled = !muted;
    });
    setCall(current => current ? {...current, muted} : current);
  }, []);

  // remote audio into the hidden <audio autoplay>
  const playRemoteAudio = (session) => {
    const peerConnection = session?.sessionDescriptionHandler?.peerConnection;
    if (!peerConnection || !audioRef.current) return;
    const stream = new MediaStream();
    peerConnection.getReceivers().forEach(receiver => receiver.track && stream.addTrack(receiver.track));
    audioRef.current.srcObject = stream;
    audioRef.current.play?.()?.catch(() => {
      // autoplay is allowed after the agent's click on Connect / Answer
    });
  };

  const onInvite = useCallback((invitation) => {
    // one call at a time
    if (sessionRef.current) {
      invitation.reject({statusCode: 486}).catch(() => {});
      return;
    }
    const request = invitation.request;
    const conversationId = request.getHeader("X-Conversation-Id") || null;
    const callType = request.getHeader("X-Call-Type") || null;
    const toNumber = request.getHeader("X-To-Number") || null;
    // a call transferred to an agent group this agent joined
    const groupId = request.getHeader("X-Group-Id") || null;
    const groupNumber = request.getHeader("X-Group-Number") || null;
    const identity = invitation.remoteIdentity;
    const caller = callType === "agent-outbound" && toNumber
      ? `Call ${toNumber}`
      : identity?.displayName || identity?.uri?.user || "Unknown caller";
    const expected = takeExpected({conversationId, callType, toNumber, caller});
    const autoAnswer = Boolean(expected);
    // "Call customer": the agent's leg is answered first, then the customer is dialed (phase from the call status)
    const outbound = Boolean(expected?.callCustomer);
    const id = request.callId;
    sessionRef.current = invitation;
    mutedRef.current = false;
    setCall({
      id, caller, conversationId, state: autoAnswer ? "connecting" : "ringing", startedAt: null,
      muted: false, projectId: projectRef.current.projectId, subProjectId: projectRef.current.subProjectId,
      subProjects: projectRef.current.subProjects || [],
      outbound, phase: outbound ? "agent_ringing" : null,
      group: callType === "agent-group" ? {id: groupId, number: groupNumber} : null,
    });

    // the group's name for the popup, from the project's groups (X-Group-Id)
    if (callType === "agent-group" && groupId && projectRef.current.projectId) {
      WebrtcRepository.getMyGroups({projectId: projectRef.current.projectId})
        .then(({data}) => {
          const name = (data?.groups || []).find(group => String(group.id) === String(groupId))?.name;
          if (name) setCall(current => current?.id === id ? {...current, group: {...current.group, name}} : current);
        })
        .catch(() => {});
    }

    // CANCEL while ringing: for a group call with Reason cause=200 another agent answered it first
    invitation.delegate = {
      onCancel: (cancel) => {
        const reason = cancel?.request?.getHeader("Reason") || "";
        if (/cause=200\b/.test(reason)) toast.info("Answered by another agent");
        else if (!autoAnswer) toast.info(`Missed call from ${caller}`);
      },
    };

    invitation.stateChange.addListener((state) => {
      if (state === SessionState.Established) {
        playRemoteAudio(invitation);
        setCall(current => current?.id === id
          ? {...current, state: "active", startedAt: current.outbound ? current.startedAt : current.startedAt || Date.now()}
          : current);
      }
      if (state === SessionState.Terminated && sessionRef.current === invitation) {
        sessionRef.current = null;
        clearAudio();
        setCall(null);
      }
    });

    if (autoAnswer) answer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answer]);

  // Asks the backend whether Asterisk sees the agent's endpoint registered; only then the phone is "Connected".
  const verifyRegistration = useCallback(async (isCurrent) => {
    const {projectId} = projectRef.current;
    for (let attempt = 0; attempt < VERIFY_ATTEMPTS; attempt++) {
      try {
        const {data} = await WebrtcRepository.getWebrtcStatus({projectId});
        if (!isCurrent() || !aliveRef.current) return;
        if (data?.registered === true) {
          setStatus("registered");
          return;
        }
      } catch (e) {
        console.error("Failed to check the WebRTC registration", e);
      }
      await new Promise(resolve => setTimeout(resolve, VERIFY_INTERVAL_MS));
      if (!isCurrent() || !aliveRef.current) return;
    }
    setStatus("failed");
    setError("Asterisk does not see your browser phone as registered");
  }, []);

  // The proxy dropped the socket: its token is single use, so sip.js can't reconnect with the same URL. A new token
  // is fetched and a new user agent started, retried every RECONNECT_DELAY_MS until it registers again or the phone
  // is stopped / reconnected.
  const reconnectThroughProxy = useCallback(() => {
    const generation = generationRef.current;
    const attempt = async () => {
      if (!aliveRef.current || generation !== generationRef.current) return;
      try {
        const {data} = await WebrtcRepository.getProxyToken({projectId: projectRef.current.projectId});
        if (!aliveRef.current || generation !== generationRef.current) return;
        const started = await startPhoneRef.current({config: configRef.current, proxy: data, reconnecting: true});
        if (!started && generation === generationRef.current) setTimeout(attempt, RECONNECT_DELAY_MS);
      } catch (e) {
        if (generation !== generationRef.current) return;
        const code = webrtcErrorCode(e);
        if (code === "sip_proxy_disabled" || code === "agent_not_provisioned") {
          // the proxy was turned off or the agent removed: a full connect fetches the config the backend wants now
          connectRef.current?.(projectRef.current);
          return;
        }
        setTimeout(attempt, RECONNECT_DELAY_MS);
      }
    };
    setTimeout(attempt, RECONNECT_DELAY_MS);
  }, []);

  // Starts a user agent and registers. proxy: a SIP proxy token ({token, wsUrl}) - the browser has no SIP login, the
  // proxy logs in to Asterisk for it; without one, project.wsUrl with the agent's own SIP login (old backend).
  // reconnecting: after the proxy dropped the socket; failing then resolves to false without marking the phone failed,
  // the caller retries. -> true when the user agent started
  const startPhone = useCallback(async ({config, proxy, reconnecting = false}) => {
    // the project's backend configuration PBX (wss://<pbx>/ws), not the ws_url ami-client returns. Through the proxy
    // the backend picks the PBX itself.
    const wsUrl = proxy
      ? `${proxy.wsUrl}${proxy.wsUrl.includes("?") ? "&" : "?"}token=${encodeURIComponent(proxy.token)}`
      : projectRef.current.wsUrl;
    if (!wsUrl) {
      setStatus("failed");
      setError("No PBX is set in this project's backend configuration");
      return false;
    }
    // the token is not logged
    const wsLabel = proxy ? proxy.wsUrl : wsUrl;

    // the previous phone (another project, or a reconnect) is dropped; the refresh timer belongs to the new config
    const previous = uaRef.current;
    uaRef.current = null;
    registererRef.current = null;
    endSession(sessionRef.current);
    sessionRef.current = null;
    previous?.stop().catch(() => {});
    // sip.js through the SIP proxy, or (old backend) straight to Asterisk with the agent's own SIP login from ami-client
    const uri = UserAgent.makeURI(config.sip_uri);
    if (!uri) {
      setStatus("failed");
      setError(`Invalid SIP address: ${config.sip_uri}`);
      return false;
    }
    let socketOpened = false;
    let registerer = null;
    const isCurrent = () => uaRef.current === ua;

    const register = () => {
      if (!isCurrent() || !registerer) return;
      registerer.register({
        requestDelegate: {
          onReject: (response) => {
            if (!isCurrent()) return;
            const {statusCode, reasonPhrase} = response.message;
            console.warn("[WebRTC] registration failed", {status: statusCode, reason: reasonPhrase, sip_uri: config.sip_uri});
            setStatus("failed");
            setError(`Registration failed: ${statusCode} ${reasonPhrase}`);
          },
        },
      }).catch(e => {
        if (!isCurrent()) return;
        console.warn("[WebRTC] registration failed", e);
        setStatus("failed");
        setError(`Registration failed: ${e?.message || e}`);
      });
    };

    const ua = new UserAgent({
      uri,
      transportOptions: {server: wsUrl},
      // through the proxy no SIP login: it answers Asterisk's challenges with the agent's password
      ...(proxy ? {} : {authorizationUsername: config.authorization_user, authorizationPassword: config.password}),
      displayName: config.display_name,
      logBuiltinEnabled: false,
      // the TURN credentials are read when a call is answered, so a refreshed config is used without reconnecting
      sessionDescriptionHandlerFactory: (session, options) => defaultFactory(session, {
        ...options, peerConnectionConfiguration: {iceServers: configRef.current?.ice_servers || []},
      }),
      delegate: {
        onInvite: (invitation) => isCurrent() ? onInvite(invitation) : invitation.reject().catch(() => {}),
        onConnect: () => {
          if (!isCurrent()) return;
          const reconnected = socketOpened;
          socketOpened = true;
          // the socket came back: the TURN credentials may have expired meanwhile, and the registration is renewed
          if (reconnected) {
            fetchConfig().catch(e => console.error("Failed to refresh the WebRTC config", e));
            register();
          }
        },
        onDisconnect: (disconnectError) => {
          if (!isCurrent() || !disconnectError) return; // no error: ua.stop()
          console.warn("[WebRTC] socket closed", {ws_url: wsLabel, error: disconnectError?.message});
          if (statusRef.current === "connecting") {
            setStatus("failed");
            setError(`Asterisk closed the connection before registering (${disconnectError.message})`);
            return;
          }
          // was connected: keep trying in the background
          setStatus("unregistered");
          setError("Connection to the phone server lost, reconnecting…");
          if (proxy) {
            reconnectThroughProxy();
            return;
          }
          const retry = () => {
            if (!isCurrent()) return;
            ua.reconnect().catch(() => setTimeout(retry, RECONNECT_DELAY_MS));
          };
          setTimeout(retry, RECONNECT_DELAY_MS);
        },
      },
    });

    uaRef.current = ua;
    try {
      await ua.start();
    } catch (e) {
      if (!isCurrent()) return false;
      console.warn("[WebRTC] socket failed", {ws_url: wsLabel, error: e?.message});
      // still "Reconnecting…": the caller tries again with a new token
      if (reconnecting) return false;
      setStatus("failed");
      // the browser can't see the handshake's HTTP status: through the proxy a 502 (no PBX / AMI config) or a
      // 403 (expired token) both end here
      setError(proxy ? "Phone server unavailable, try Connect again in a moment" : `Cannot reach the phone server (${wsUrl})`);
      return false;
    }
    if (!isCurrent()) return false;
    registerer = new Registerer(ua, {expires: REGISTER_EXPIRES});
    registererRef.current = registerer;
    registerer.stateChange.addListener((state) => {
      if (!isCurrent()) return;
      if (state === RegistererState.Registered) {
        setError(null);
        setStatus("verifying");
        verifyRegistration(isCurrent);
      }
      if (state === RegistererState.Unregistered && statusRef.current !== "failed") setStatus("unregistered");
    });
    register();
    setWebrtcEnabled(currentUser?.username, true);
    return true;
  }, [currentUser?.username, fetchConfig, onInvite, verifyRegistration, reconnectThroughProxy]);
  startPhoneRef.current = startPhone;

  // Starts (or restarts) the browser phone: microphone permission, fresh config, register.
  // project: {projectId, subProjectId, wsUrl} the agent is in: wsUrl is the PBX WebSocket to register with when the
  // backend has no SIP proxy; projectId / subProjectId are used for calls and for opening a call's conversation.
  const connect = useCallback(async (project = {}) => {
    generationRef.current += 1;
    projectRef.current = project;
    setStatus("connecting");
    setError(null);
    const micError = await requestMicrophone();
    if (micError) {
      setStatus("failed");
      setError(micError);
      toast.error(micError, {toastId: "webrtc-microphone", autoClose: 10000});
      return false;
    }
    let config;
    try {
      config = await fetchConfig();
    } catch (e) {
      console.error(e);
      setStatus("failed");
      setError(webrtcErrorMessage(e, e?.message || "Could not load your browser calling settings"));
      return false;
    }
    if (!aliveRef.current) return false;
    return startPhone({config, proxy: config.proxy});
  }, [fetchConfig, startPhone]);
  connectRef.current = connect;

  // The registry project page the agent is on. The WebRTC identity is per project, so a running phone reconnects
  // only for another project (or another PBX); another sub project only changes where calls are placed / opened.
  const attachProject = useCallback((project) => {
    if (!project?.projectId) return;
    const changed = String(project.projectId) !== String(projectRef.current.projectId)
      || project.wsUrl !== projectRef.current.wsUrl;
    projectRef.current = project;
    if (changed && uaRef.current) connect(project);
  }, [connect]);

  const disconnect = useCallback(() => {
    setWebrtcEnabled(currentUser?.username, false);
    stop();
  }, [currentUser?.username, stop]);

  // Calls only go through the browser phone (no VDN fallback): false, with a message, when it is not Connected for
  // this project.
  const ensureConnected = (projectId) => {
    if (statusRef.current === "registered" && projectId && String(projectId) === String(projectRef.current.projectId)) {
      return true;
    }
    toast.error("Your browser phone is not connected. Click Connect in the header first.");
    return false;
  };

  // "Take over call (WebRTC)" on an ACTIVE conversation: the caller is bridged to this browser (answered
  // automatically). Resolves to the handover response, if any.
  // projectId: the conversation's registry project, the phone must be connected for it.
  const takeOverCall = useCallback(async ({projectId, conversationId}) => {
    if (!ensureConnected(projectId)) return;
    const expected = expectCall({conversationId});
    try {
      const response = await WebrtcRepository.handoverToWebrtc({projectId, conversationId});
      toast.info("Connecting the call to your browser…");
      return response;
    } catch (e) {
      forgetCall(expected);
      toast.error(webrtcErrorCode(e) === "agent_not_registered"
        ? "Asterisk does not see your browser phone as registered. Reconnect and try again."
        : webrtcErrorMessage(e, "Failed to take over the call"));
    }
  }, []);

  // "Call customer": this browser rings first (answered automatically, shown as "Call <number>"), then the customer
  // is dialed from the sub project's outbound number.
  // subProjectId: whose outbound number / trunk to use for a typed number (default: the current sub project)
  const callCustomer = useCallback(async ({projectId, conversationId, phoneNumber, subProjectId: chosenSubProjectId}) => {
    const subProjectId = chosenSubProjectId || projectRef.current.subProjectId;
    if (!ensureConnected(projectId)) return;
    const expected = expectCall({conversationId, number: phoneNumber, callCustomer: true});
    let actionId;
    try {
      // a conversation is called from its own sub project (the backend's default), a typed number from the current one
      const {data} = await WebrtcRepository.webrtcCallCustomer({
        projectId, subProjectId: conversationId ? undefined : subProjectId, conversationId, phoneNumber,
      });
      actionId = data?.action_id;
      phoneNumber = data?.to_number || phoneNumber;
      // the number actually dialed, matched against X-To-Number
      expected.number = phoneNumber;
    } catch (e) {
      forgetCall(expected);
      // 400 / 404 / 409 / 422 (no number, not registered, no outbound number, ...)
      toast.error(webrtcErrorCode(e) === "agent_not_registered"
        ? "Asterisk does not see your browser phone as registered. Reconnect and try again."
        : webrtcErrorMessage(e, `Failed to call ${phoneNumber || "the customer"}`));
      return;
    }
    const number = phoneNumber || "the customer";
    const toastId = toast.loading(`Calling ${number}…`);
    if (!actionId) {
      toast.update(toastId, {render: `Calling ${number}`, type: "info", isLoading: false, autoClose: 4000});
      return;
    }
    const finish = (render, type) => toast.update(toastId, {render, type, isLoading: false, autoClose: 5000});
    // the browser call's phase, shown in the call popup
    const setPhase = (phase) => setCall(current => current?.outbound ? {
      ...current, phase,
      startedAt: phase === "customer_answered" ? current.startedAt || Date.now() : current.startedAt,
    } : current);
    // agent_ringing -> agent_answered -> customer_ringing -> customer_answered -> ended (or agent_failed). Polled
    // until the customer answers or the call ends; the talk itself ends with the browser session.
    const startedAt = Date.now();
    let lastPhase = null;
    while (aliveRef.current && Date.now() - startedAt < POLL_TIMEOUT_MS) {
      await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
      let data;
      try {
        ({data} = await WebrtcRepository.pollCallStatus({projectId, actionId}));
      } catch (e) {
        console.error("Failed to poll the call status", e);
        continue;
      }
      const phase = data?.phase;
      if (phase && phase !== lastPhase) {
        lastPhase = phase;
        setPhase(phase);
      }
      if (phase === "agent_failed" || (!phase && data?.status === "done" && data?.outcome !== "answered")) {
        finish(`Call to ${number} failed: ${outcomeLabel(data?.outcome)}`, "error");
        return;
      }
      if (phase === "customer_ringing" || phase === "agent_answered") {
        toast.update(toastId, {render: `Ringing ${number}…`});
      }
      if (phase === "customer_answered") {
        finish(`${number} answered`, "success");
        return;
      }
      if (phase === "ended") {
        const outcome = data?.customer?.outcome;
        if (outcome === "answered") finish(`Call with ${number} ended`, "info");
        else finish(`${number} did not answer: ${outcomeLabel(outcome)}`, "warning");
        return;
      }
      // older sip-connector without phases: only the agent's leg is reported
      if (!phase && data?.status === "done") {
        finish(`Dialing ${number}…`, "success");
        return;
      }
    }
    finish(lastPhase === "customer_ringing" ? `${number} did not answer` : "No result for the call yet", "warning");
  }, []);

  // Sends the caller of the answered call to a bot extension ({extensionId, initMessage}) or a number ({number}).
  // Asterisk then hangs up the agent's leg (BYE), which ends the call as usual. -> true when redirected
  const redirectCall = useCallback(async ({extensionId, number, initMessage}) => {
    const current = sessionRef.current;
    const conversationId = current?.request?.getHeader("X-Conversation-Id");
    const {projectId} = projectRef.current;
    if (!current || !conversationId || !projectId) {
      toast.error("This call cannot be redirected");
      return false;
    }
    try {
      await WebrtcRepository.redirectCall({projectId, conversationId, extensionId, number, initMessage});
      toast.success(extensionId ? "Call redirected to the bot" : `Call redirected to ${number}`);
      return true;
    } catch (e) {
      const code = webrtcErrorCode(e);
      toast.error(code === "conversation_not_active"
        ? "This call is no longer active and cannot be redirected"
        : e?.response?.status === 503
          ? "Asterisk is unavailable right now, try again"
          : webrtcErrorMessage(e, "Failed to redirect the call"));
      return false;
    }
  }, []);

  // While connected, re-checks Asterisk's live PJSIP state (a lost network is only noticed by Asterisk's qualify,
  // 30-60s later). registered: true | false | null (null: sip-connector could not reach Asterisk) -> "Reconnecting…"
  // until it is true again. Not while on a call, the agent's audio is the better signal then.
  useEffect(() => {
    if (status !== "registered" && status !== "unregistered") return;
    const id = setInterval(async () => {
      if (registererRef.current?.state !== RegistererState.Registered || sessionRef.current) return;
      try {
        const {data} = await WebrtcRepository.getWebrtcStatus({projectId: projectRef.current.projectId});
        if (data?.registered === true) {
          setStatus("registered");
          setError(null);
        } else {
          setStatus("unregistered");
          setError(data?.registered === false
            ? "Asterisk no longer sees your browser phone"
            : "Asterisk cannot be reached right now");
        }
      } catch (e) {
        // keep the current state, the next check retries
      }
    }, HEALTH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [status]);

  // logged out or another user: the phone belongs to the previous one
  useEffect(() => {
    stop();
  }, [currentUser?.username, stop]);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      stop();
    };
  }, [stop]);

  const value = useMemo(() => ({
    status, error, call, connect, disconnect, attachProject, answer, reject, hangup, toggleMute, takeOverCall, callCustomer,
    redirectCall,
  }), [status, error, call, connect, disconnect, attachProject, answer, reject, hangup, toggleMute, takeOverCall,
    callCustomer, redirectCall]);

  return (
    <WebrtcContext.Provider value={value}>
      {children}
      <audio ref={audioRef} autoPlay hidden/>
      <WebrtcCallPopup/>
    </WebrtcContext.Provider>
  );
};
